import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "../store";
import type { OverviewDomainsAggRow } from "../overview/types";
import { mapOverviewDomainsToTableRows } from "../overview/utils/mapOverviewDomainsToTableRows";
import type { SourceUsageTableRow } from "../../components/SourceTable/types";
import { normalizeOverviewSourceUsageChart } from "../../view/Overview/utils/normalizeOverviewSourceUsageChart";
import { getDateRangeFromOverviewFilter } from "../../view/Overview/utils/getDateRangeFromOverviewFilter";
import { normalizeSourceDetailUrlsChart } from "../overview/utils/normalizeSourceDetailUrlsChart";

export const selectSelectedModelIdState = (state: RootState) => state.overview.selectedModelId;
export const selectOverviewDateFilterState = (state: RootState) => state.overview.dateFilter;

const EMPTY_OVERVIEW_DOMAINS: OverviewDomainsAggRow[] = [];

export const selectOverviewDomainsRawData = (state: RootState): OverviewDomainsAggRow[] =>
  state.overview.overviewDomains.data ?? EMPTY_OVERVIEW_DOMAINS;

export const selectOverviewDomainsRows = createSelector(
  selectOverviewDomainsRawData,
  (raw): SourceUsageTableRow[] => mapOverviewDomainsToTableRows(raw)
);

const OVERVIEW_TOP_ROWS_LIMIT = 5;

export const selectOverviewDomainsRowsTop = createSelector(
  selectOverviewDomainsRows,
  (rows): SourceUsageTableRow[] => rows.slice(0, OVERVIEW_TOP_ROWS_LIMIT)
);

export const selectOverviewSourceUsageChart = createSelector(
  selectOverviewDomainsRawData,
  selectOverviewDateFilterState,
  (raw, dateFilter) =>
    normalizeOverviewSourceUsageChart(raw, getDateRangeFromOverviewFilter(dateFilter))
);

/** Raw data for Source page chart: when search is active uses sourceDomainsChartData, else overview. */
const selectSourcePageChartRawData = (state: RootState): OverviewDomainsAggRow[] => {
  const chartData = state.overview.sourceDomainsChartData;
  if (chartData !== null) return chartData;
  return state.overview.overviewDomains.data ?? EMPTY_OVERVIEW_DOMAINS;
};

/** Chart data for Source page (respects search filter when sourceDomainsChartData is set). */
export const selectSourcePageChart = createSelector(
  selectSourcePageChartRawData,
  selectOverviewDateFilterState,
  (raw, dateFilter) =>
    normalizeOverviewSourceUsageChart(raw, getDateRangeFromOverviewFilter(dateFilter))
);

export const selectSourceDomainsChartDataLoading = (state: RootState) =>
  state.overview.sourceDomainsChartDataLoading;

export const selectOverviewDomainsLoading = (state: RootState) =>
  state.overview.overviewDomains.loading === "loading";

/** True when fetch is in progress or not started yet (idle, no data). Never true when loading === "error". */
export const selectOverviewSourceUsageShowSkeleton = createSelector(
  (state: RootState) => state.overview.overviewDomains.loading,
  (state: RootState) => state.overview.overviewDomains.data,
  (loading, data) => {
    if (loading === "error") return false;
    return (
      loading === "loading" ||
      (loading === "idle" && (data === null || data.length === 0))
    );
  }
);

export const selectOverviewDomainsError = (state: RootState) =>
  state.overview.overviewDomains.error;

export const selectSourceDomainsPageState = (state: RootState) =>
  state.overview.sourceDomainsPage;

/** Raw rows for Source table: chart data when search active, otherwise overview domains. */
const selectSourceTableRawData = (state: RootState): OverviewDomainsAggRow[] => {
  const chart = state.overview.sourceDomainsChartData;
  if (chart !== null) return chart;
  return state.overview.overviewDomains.data ?? EMPTY_OVERVIEW_DOMAINS;
};

/** All domains for Source table (aggregated). Used to paginate by domain, not by view row. */
const selectSourceDomainsAggregatedRows = createSelector(
  selectSourceTableRawData,
  (raw): SourceUsageTableRow[] => mapOverviewDomainsToTableRows(raw)
);

export const selectSourceDomainsPageRows = createSelector(
  selectSourceDomainsAggregatedRows,
  (state: RootState) => state.overview.sourceDomainsPage.page,
  (state: RootState) => state.overview.sourceDomainsPage.pageSize,
  (allRows, page, pageSize): SourceUsageTableRow[] => {
    if (allRows.length === 0) return [];
    const start = (page - 1) * pageSize;
    return allRows.slice(start, start + pageSize).map((row, i) => ({
      ...row,
      index: start + i + 1,
    }));
  }
);

export const selectSourceDomainsPageTotal = createSelector(
  selectSourceDomainsAggregatedRows,
  (rows): number => rows.length
);

export const selectSourceDomainsPageTotalPages = createSelector(
  selectSourceDomainsPageTotal,
  (state: RootState) => state.overview.sourceDomainsPage.pageSize,
  (total, pageSize) =>
    pageSize > 0 ? Math.ceil(total / pageSize) : 1
);

/** One row per date for a domain: links and citations for source detail "Pages" table. */
export interface SourceDetailLinkRow {
  date: string;
  dateDisplay: string;
  linksCount: number;
  citationsCount: number;
}

const formatDateDisplay = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export const selectSourceDetailLinksByDate = (domainId: number) =>
  createSelector(
    selectOverviewDomainsRawData,
    (raw): SourceDetailLinkRow[] => {
      const byDate = raw
        .filter((r) => r.domain_id === domainId)
        .reduce(
          (acc, r) => {
            const cur = acc.get(r.response_date);
            if (cur) {
              cur.linksCount += r.links_count;
              cur.citationsCount += r.citations_count;
            } else {
              acc.set(r.response_date, {
                date: r.response_date,
                dateDisplay: formatDateDisplay(r.response_date),
                linksCount: r.links_count,
                citationsCount: r.citations_count,
              });
            }
            return acc;
          },
          new Map<string, SourceDetailLinkRow>()
        );
      return [...byDate.values()].sort(
        (a, b) => a.date.localeCompare(b.date)
      );
    }
  );

/** Chart data for a single domain (source detail page) — from overview domains. */
export const selectSourceDetailChartByDomainId = (domainId: number) =>
  createSelector(
    selectOverviewDomainsRawData,
    selectOverviewDateFilterState,
    (raw, dateFilter) => {
      const domainRows = raw.filter((r) => r.domain_id === domainId);
      return normalizeOverviewSourceUsageChart(
        domainRows,
        getDateRangeFromOverviewFilter(dateFilter)
      );
    }
  );

/** Chart data for source detail URLs: top 5 URLs by usage, all lines green, from links. */
export const selectSourceDetailUrlsChart = createSelector(
  (state: RootState) => state.overview.sourceDetailLinks.data,
  normalizeSourceDetailUrlsChart
);

export const selectSourceDetailLinksState = (state: RootState) =>
  state.overview.sourceDetailLinks;
